<div class="machining-journal">
  <div class="container-fluid pt-4">
    <a class="back-btn" routerLink="/machining">← Machine Shop</a>
    <h1 class="page-title">Session Journal</h1>

    <div class="streak-card">
      <span class="streak-label">{{ streakLabel }}</span>
      <button class="btn-new" (click)="openNewForm()">+ New session</button>
    </div>

    <div *ngIf="loading" class="empty-state">Loading…</div>
    <div *ngIf="!loading && entries.length === 0" class="empty-state">
      No sessions logged yet. Hit "+ New session" to start your log.
    </div>

    <div *ngFor="let entry of entries" class="entry-card">
      <div class="entry-head">
        <div>
          <div class="entry-date">{{ formatDate(entry.date) }}</div>
          <div class="entry-meta">
            <span class="machine-pill"
                  [class.lathe]="entry.machine === 'lathe'"
                  [class.mill]="entry.machine === 'mill'"
                  [class.both]="entry.machine === 'both'">
              {{ entry.machine | titlecase }}
            </span>
            <span *ngIf="entry.moduleId" class="module-link">{{ getModuleTitle(entry.moduleId) }}</span>
            <span *ngIf="entry.project" class="entry-project">· {{ entry.project }}</span>
            <span *ngIf="entry.material" class="entry-material">· {{ entry.material }}</span>
            <span *ngIf="entry.sessionLength" class="entry-length">· {{ entry.sessionLength }}</span>
          </div>
        </div>
        <div class="entry-actions">
          <button class="link-btn" (click)="openEditForm(entry)">Edit</button>
          <button class="link-btn danger" (click)="deleteEntry(entry)">Delete</button>
        </div>
      </div>

      <div *ngIf="entry.operations?.length" class="operations-table-wrap">
        <table class="operations-table">
          <thead>
            <tr><th>Operation</th><th>RPM</th><th>Feed</th><th>Notes</th></tr>
          </thead>
          <tbody>
            <tr *ngFor="let op of entry.operations">
              <td>{{ operationLabel(op.type) }}</td>
              <td>{{ op.rpm ?? '—' }}</td>
              <td>{{ op.feed || '—' }}</td>
              <td>{{ op.notes || '' }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div *ngIf="entry.whatWentWell" class="entry-block">
        <div class="entry-block-label">What went well</div>
        <div class="entry-block-text">{{ entry.whatWentWell }}</div>
      </div>
      <div *ngIf="entry.challenges" class="entry-block">
        <div class="entry-block-label">Challenges</div>
        <div class="entry-block-text">{{ entry.challenges }}</div>
      </div>
      <div *ngIf="entry.notes" class="entry-block">
        <div class="entry-block-label">Notes</div>
        <div class="entry-block-text">{{ entry.notes }}</div>
      </div>

      <div class="photo-strip-wrap">
        <div *ngIf="getPhotos(entry.id).length" class="photo-strip">
          <button type="button"
                  *ngFor="let photo of getPhotos(entry.id)"
                  class="photo-thumb"
                  (click)="openLightbox(photo)">
            <img [src]="photo.downloadUrl" [alt]="photo.caption || ''" loading="lazy">
          </button>
        </div>
        <label class="photo-add">
          <input type="file" accept="image/*" multiple (change)="addPhotosToEntry($event, entry)">
          <span>+ Add photos</span>
        </label>
      </div>
    </div>

    <div *ngIf="showForm" class="modal-backdrop" (click)="closeForm()">
      <div class="modal-card" (click)="$event.stopPropagation()">
        <div class="modal-header-row">
          <h5>{{ editingEntry ? 'Edit Session' : 'New Session' }}</h5>
          <button class="btn-close-modal" (click)="closeForm()">✕</button>
        </div>
        <div class="modal-body-inner">

          <div class="form-row">
            <div class="form-group">
              <label>Date</label>
              <input type="date" class="form-control" [(ngModel)]="formData.date">
            </div>
            <div class="form-group">
              <label>Machine</label>
              <select class="form-control" [(ngModel)]="formData.machine" (ngModelChange)="onMachineChange()">
                <option value="lathe">Lathe</option>
                <option value="mill">Mill</option>
                <option value="both">Both</option>
              </select>
            </div>
          </div>

          <div class="form-group">
            <label>Module <span class="muted">(optional)</span></label>
            <select class="form-control" [(ngModel)]="formData.moduleId">
              <option [ngValue]="undefined">— none —</option>
              <option *ngFor="let m of modulesForSelectedMachine" [value]="m.id">
                {{ m.machine | titlecase }} #{{ m.number }} — {{ m.title }}
              </option>
            </select>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>Project / part</label>
              <input type="text" class="form-control" [(ngModel)]="formData.project" placeholder="e.g. drill arbor">
            </div>
            <div class="form-group">
              <label>Material</label>
              <input type="text" class="form-control" [(ngModel)]="formData.material" placeholder="e.g. 1018 steel">
            </div>
          </div>

          <div class="form-group">
            <label>Session length <span class="muted">(optional)</span></label>
            <input type="text" class="form-control" [(ngModel)]="formData.sessionLength" placeholder="e.g. 2h, 45 min">
          </div>

          <div class="form-group">
            <label>Operations <span class="muted">(speeds &amp; feeds — add a row per operation you ran)</span></label>
            <div *ngIf="formData.operations?.length" class="ops-editor">
              <div *ngFor="let op of formData.operations; let i = index" class="ops-row">
                <select class="form-control op-type" [(ngModel)]="op.type" [name]="'op-type-' + i">
                  <option *ngFor="let t of availableOpsForMachine" [value]="t">{{ operationLabel(t) }}</option>
                </select>
                <input type="number" class="form-control op-rpm" [(ngModel)]="op.rpm" [name]="'op-rpm-' + i" placeholder="RPM">
                <input type="text" class="form-control op-feed" [(ngModel)]="op.feed" [name]="'op-feed-' + i" placeholder="Feed (e.g. 0.005 ipr)">
                <input type="text" class="form-control op-notes" [(ngModel)]="op.notes" [name]="'op-notes-' + i" placeholder="Notes">
                <button type="button" class="op-remove" (click)="removeOperation(i)" aria-label="Remove operation">×</button>
              </div>
            </div>
            <button type="button" class="op-add-btn" (click)="addOperation()">+ Add operation</button>
          </div>

          <div class="form-group">
            <label>What went well</label>
            <textarea class="form-control" rows="2" [(ngModel)]="formData.whatWentWell"></textarea>
          </div>

          <div class="form-group">
            <label>Challenges</label>
            <textarea class="form-control" rows="2" [(ngModel)]="formData.challenges"></textarea>
          </div>

          <div class="form-group">
            <label>Notes</label>
            <textarea class="form-control" rows="3" [(ngModel)]="formData.notes"></textarea>
          </div>

          <div class="form-group">
            <label>Photos <span class="muted">(up to 10)</span></label>
            <div class="photo-previews" *ngIf="filePreviews.length">
              <div *ngFor="let src of filePreviews; let i = index" class="photo-preview">
                <img [src]="src">
                <button type="button" class="photo-preview-x" (click)="removeSelectedFile(i)">×</button>
              </div>
            </div>
            <label class="file-picker">
              <input type="file" accept="image/*" multiple (change)="onFilesSelected($event)">
              <span>📷 Add photos</span>
            </label>
            <div *ngIf="uploadProgress > 0 && uploadProgress < 100" class="upload-progress">
              Uploading… {{ uploadProgress }}%
            </div>
          </div>

          <div *ngIf="formError" class="alert alert-danger mt-2">{{ formError }}</div>
        </div>

        <div class="modal-footer-row">
          <button class="btn-secondary" (click)="closeForm()" [disabled]="saving">Cancel</button>
          <button class="btn-primary" (click)="saveEntry()" [disabled]="saving">
            {{ saving ? 'Saving…' : 'Save' }}
          </button>
        </div>
      </div>
    </div>
    <div *ngIf="lightboxPhoto" class="lightbox-backdrop" (click)="closeLightbox()">
      <div class="lightbox" (click)="$event.stopPropagation()">
        <button class="lightbox-close" (click)="closeLightbox()">✕</button>
        <img [src]="lightboxPhoto.downloadUrl" [alt]="lightboxPhoto.caption || ''">
        <div class="lightbox-meta">
          <ng-container *ngIf="editingCaptionId === lightboxPhoto.id; else captionView">
            <input type="text" class="caption-input" [(ngModel)]="captionDraft" placeholder="Caption">
            <button class="btn-tiny" (click)="saveCaption(lightboxPhoto)">Save</button>
            <button class="btn-tiny ghost" (click)="editingCaptionId = null">Cancel</button>
          </ng-container>
          <ng-template #captionView>
            <span class="lightbox-caption">{{ lightboxPhoto.caption || '— no caption —' }}</span>
            <button class="btn-tiny ghost" (click)="startEditCaption(lightboxPhoto)">Edit caption</button>
            <button class="btn-tiny danger" (click)="deletePhoto(lightboxPhoto)">Delete</button>
          </ng-template>
        </div>
      </div>
    </div>
  </div>
</div>
